import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { adminLogout, getOrderCounts } from '../services/adminService';

const AdminSidebar = ({ counts: countsProp }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  // Orders sidebar item shows a red badge with the count of orders that need
  // admin attention. Parent pages may pass counts down as a prop; otherwise we
  // fetch a lightweight counts payload on mount.
  const [counts, setCounts] = useState(countsProp || null);
  useEffect(() => {
    if (countsProp) {
      setCounts(countsProp);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const c = await getOrderCounts(30);
        if (!cancelled) setCounts(c);
      } catch (_) {
        // silent — sidebar badge is nice-to-have
      }
    })();
    return () => { cancelled = true; };
  }, [countsProp]);

  const needsAttention = counts?.needs_attention ?? 0;

  const handleLogout = () => {
    adminLogout();
  };

  const navItems = [
    {
      path: '/admin',
      label: 'Dashboard',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
          <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
          <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
          <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
          <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      )
    },
    {
      path: '/admin/orders',
      label: 'Orders',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
          <path d="M3 2h10a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M5 6h6M5 9h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ),
      badge: needsAttention > 0 ? needsAttention : null,
    },
    {
      path: '/admin/recruiters',
      label: 'Recruiters',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
          <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M2 14c0-2.5 2.5-4.5 6-4.5s6 2 6 4.5" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      )
    },
    {
      path: '/admin/inbox',
      label: 'Inbox',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
          <path d="M2 4h12v8a1 1 0 01-1 1H3a1 1 0 01-1-1V4z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M2 4l6 5 6-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      path: '/admin/feedback',
      label: 'Feedback',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
          <path d="M8 1l2 4 4.5.5-3.3 3 .8 4.5L8 11l-4 2 .8-4.5L1.5 5.5 6 5l2-4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      path: '/admin/revenue',
      label: 'Revenue',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
          <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M8 4v8M5.5 6h4a1.5 1.5 0 010 3h-3a1.5 1.5 0 000 3h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      path: '/admin/pricing',
      label: 'Pricing',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
          <path d="M2 2h7l5 5-6 6-5-5V2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <circle cx="5.5" cy="5.5" r="1" fill="currentColor"/>
        </svg>
      )
    },
    {
      path: '/admin/marketing',
      label: 'Marketing',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
          <path d="M2 6v4a1 1 0 001 1h1.5l4 3V2l-4 3H3a1 1 0 00-1 1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M11.5 5.5a3 3 0 010 5M13.5 3.5a6 6 0 010 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      )
    },
    {
      path: '/admin/analytics',
      label: 'Analytics',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
          <path d="M2 13.5V9M6 13.5V4M10 13.5V6.5M14 13.5V2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      )
    },
    {
      path: '/admin/action-plan',
      label: 'Action Plan',
      icon: (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
          <path d="M4 1.5h6l2.5 2.5v10a1 1 0 01-1 1h-8a1 1 0 01-1-1v-11a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M5.5 8l1.7 1.7L10.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
  ];

  const isActive = (path) => {
    if (path === '/admin') return currentPath === '/admin';
    return currentPath.startsWith(path);
  };

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-56 z-50 flex flex-col" style={{ background: '#0D1F3C' }}>
      {/* Brand */}
      <div className="flex items-center gap-2.5 px-5 py-5 border-b" style={{ borderColor: '#1E3A5F' }}>
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: '#1A6FD6' }}></div>
        <span className="font-bold text-white text-base tracking-tight">Successlane</span>
      </div>
      <div className="px-5 py-2">
        <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#475569' }}>Admin Panel</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-2 space-y-0.5">
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all"
              style={{
                background: active ? 'rgba(26,111,214,0.18)' : 'transparent',
                color: active ? '#60A5FA' : '#94A3B8',
                borderLeft: active ? '2px solid #1A6FD6' : '2px solid transparent',
              }}
            >
              {item.icon}
              <span className="flex-1">{item.label}</span>
              {item.badge != null && (
                <span
                  className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                  style={{ background: '#dc2626', color: 'white', minWidth: 20, textAlign: 'center' }}
                >
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t" style={{ borderColor: '#1E3A5F' }}>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-sm transition-colors w-full px-3 py-2 rounded-lg hover:bg-white/5"
          style={{ color: '#64748B' }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 16 16">
            <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3m0 0l-3-3m3 3H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Sign out
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
